import { useMemo } from "react";
import { TimeSlot } from "types/time-slot";
import { WebRule } from "types/web-rule";
import { useBookingStateContext } from "hooks/use-booking-state";
import {
    getBookableTimes,
    getWaitListTimes,
    getBookableTimeSets,
    getWaitListTimeSets,
} from "utils/time-slot-filters";

/**
 * Hook that filters time slots based on bookability and waitlistability
 * @param times The time slots to filter
 * @returns Bookable and waitlistable time slots
 */
function useTimeSlotFilters(times: TimeSlot[]) {
    const { guests } = useBookingStateContext();

    const bookableTimes = useMemo(() => {
        return getBookableTimes(times, guests);
    }, [times, guests]);

    const waitListTimes = useMemo(() => {
        return getWaitListTimes(times, guests);
    }, [times, guests]);

    return { bookableTimes, waitListTimes };
}

/**
 * Hook that filters multiple time sets based on bookability and waitlistability
 * @param timeSets Multiple time sets to filter
 * @returns Filtered collections of time sets with only bookable or waitlistable times
 */
function useTimeSetsFilters(timeSets: WebRule[]) {
    const { guests } = useBookingStateContext();

    const bookableTimeSets = useMemo(() => {
        return getBookableTimeSets(timeSets, guests);
    }, [timeSets, guests]);

    const waitListTimeSets = useMemo(() => {
        return getWaitListTimeSets(timeSets, guests);
    }, [timeSets, guests]);

    return { bookableTimeSets, waitListTimeSets };
}

export { useTimeSlotFilters, useTimeSetsFilters };
